import React, { useState, useRef } from "react"
import { formatAmount } from "../Dashboarding.functions/formatAmount"
import "./SimpleTable.css"

const SimpleTable = ({ title, data, columns, showTotal = false, onRowClick, getRowClass }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' })
  const [columnWidths, setColumnWidths] = useState(() => {
    // Largeurs initiales par défaut selon le type de colonne
    const initialWidths = {}
    columns.forEach((col, index) => {
      if (col.key === 'image') {
        initialWidths[col.key] = 40 // Colonne image très petite
      } else if (index === 0 && col.key !== 'image') {
        initialWidths[col.key] = 180 // Première colonne (noms) plus large
      } else {
        initialWidths[col.key] = 90 // Autres colonnes numériques
      }
    })
    return initialWidths
  })

  const tableRef = useRef(null)
  const resizingRef = useRef(null)

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return data

    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key]
      const bVal = b[sortConfig.key]
      
      // Gestion des nombres
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal
      }
      
      // Gestion des strings
      const aStr = String(aVal || '').toLowerCase()
      const bStr = String(bVal || '').toLowerCase()
      
      if (sortConfig.direction === 'asc') {
        return aStr.localeCompare(bStr)
      } else {
        return bStr.localeCompare(aStr)
      }
    })
  }, [data, sortConfig])

  const calculateTotals = React.useCallback((inputData, tableColumns) => {
    const totalsObj = {}
    tableColumns.forEach(col => {
      if (col.key !== 'country'
        && col.key !== 'client'
        && col.key !== 'product'
        && col.key !== 'image'
        && col.key !== 'varPct') {
        totalsObj[col.key] = inputData.reduce((sum, row) => {
          const value = row[col.key]
          return sum + (typeof value === 'number' ? value : 0)
        }, 0)
      }
    })
    return totalsObj
  }, [])

  const calculateVariation = React.useCallback((totalsObj, tableColumns) => {
    const yearColumns = tableColumns.filter(col => col.key !== 'country'
      && col.key !== 'client'
      && col.key !== 'product'
      && col.key !== 'image'
      && col.key !== 'varPct')
    
    if (yearColumns.length >= 2) {
      const currentYearKey = yearColumns[yearColumns.length - 1].key
      const previousYearKey = yearColumns[yearColumns.length - 2].key
      const currentTotal = totalsObj[currentYearKey]
      const previousTotal = totalsObj[previousYearKey]
      
      if (currentTotal && currentTotal !== 0) {
        return ((previousTotal - currentTotal) / currentTotal) * 100
      }
    }
    return null
  }, [])

  // Calculer les totaux pour les colonnes numériques et la variation
  const totals = React.useMemo(() => {
    if (!showTotal || !data.length) return {}
    const totalsObj = calculateTotals(data, columns)
    totalsObj.varPct = calculateVariation(totalsObj, columns)
    return totalsObj
  }, [data, columns, showTotal, calculateTotals, calculateVariation])

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }))
  }

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '↕'
    return sortConfig.direction === 'asc' ? '↑' : '↓'
  }

  const handleMouseDown = (e, columnKey) => {
    e.preventDefault()
    resizingRef.current = { columnKey, startX: e.clientX, startWidth: columnWidths[columnKey] }
    
    const handleMouseMove = (event) => {
      if (!resizingRef.current) return
      const diff = event.clientX - resizingRef.current.startX
      const newWidth = Math.max(50, resizingRef.current.startWidth + diff) // Largeur minimum 50px
      
      setColumnWidths(prev => ({
        ...prev,
        [resizingRef.current.columnKey]: newWidth
      }))
    }
    
    const handleMouseUp = () => {
      resizingRef.current = null
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
    
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  // Fonction pour afficher la variation formatée dans la ligne total
  const renderTotalVarPct = (value) => {
    if (value === null || value === undefined) return ''
    const className = value < 0 ? 'dashboard-table-cell-varneg' : 'dashboard-table-cell-varpos'
    return <span className={className}>{value.toFixed(2)} %</span>
  }

  return (
    <div className="dashboard-section">
      <h2 className="dashboard-section-title">{title}</h2>
      <div className="dashboard-table-container">
        <div className="dashboard-table-header-container">
          <table className="dashboard-table-header-table" style={{ tableLayout: 'fixed' }}>
            <thead>
              <tr className="dashboard-table-header">
                {columns.map((col, index) => (
                  <th 
                    key={col.key} 
                    className={`${col.align === 'left' ? 'dashboard-table-cell-left' : 'dashboard-table-cell-right'} dashboard-sortable-header`}
                    onClick={() => handleSort(col.key)}
                    style={{ 
                      cursor: 'pointer', 
                      userSelect: 'none',
                      width: `${columnWidths[col.key]}px`,
                      position: 'relative'
                    }}
                  >
                    {col.label} <span style={{ fontSize: '8px', marginLeft: '2px' }}>{getSortIcon(col.key)}</span>
                    {index < columns.length - 1 && (
                      <div
                        className="column-resizer"
                        onMouseDown={(e) => handleMouseDown(e, col.key)}
                        style={{
                          position: 'absolute',
                          right: 0,
                          top: 0,
                          bottom: 0,
                          width: '4px',
                          cursor: 'col-resize',
                          backgroundColor: 'transparent',
                          zIndex: 10
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#d1d5db'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
          </table>
        </div>
        
        <div className="dashboard-table-body-container">
          <table ref={tableRef} className="dashboard-table" style={{ tableLayout: 'fixed' }}>
            <tbody>
              {sortedData.length === 0 ? (
                <tr>
                  <td 
                    colSpan={columns.length} 
                    className="dashboard-table-cell dashboard-table-cell-empty"
                    style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280', fontStyle: 'italic' }}
                  >
                    No data to display
                  </td>
                </tr>
              ) : (
                sortedData.map((row, i) => {
                  const rowClass = getRowClass ? getRowClass(row) : ''
                  return (
                    <tr
                      key={i}
                      className={i % 2 === 0 ? `dashboard-table-row-alt ${rowClass}`.trim() : rowClass}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      style={onRowClick ? { cursor: 'pointer' } : undefined}
                    >
                      {columns.map(col => (
                        <td 
                          key={col.key} 
                          className={`dashboard-table-cell ${col.align === 'left' ? 'dashboard-table-cell-left' : 'dashboard-table-cell-right'}`}
                          style={{ width: `${columnWidths[col.key]}px` }}
                        >
                          {col.render ? col.render(row[col.key], row) : row[col.key]}
                        </td>
                      ))}
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {showTotal && Object.keys(totals).length > 0 && (
          <div className="dashboard-table-total-container">
            <table className="dashboard-table-total-table" style={{ tableLayout: 'fixed' }}>
              <tbody>
                <tr className="dashboard-table-total-row">
                  {columns.map(col => (
                    <td
                      key={col.key}
                      className={`dashboard-table-cell dashboard-table-cell-total ${col.align === 'left' ? 'dashboard-table-cell-left' : 'dashboard-table-cell-right'}`}
                      style={{ width: `${columnWidths[col.key]}px` }}
                    >
                      {col.key === 'country' || col.key === 'client' || col.key === 'product' ? (
                        <strong>TOTAL</strong>
                      ) : col.key === 'image' ? (
                        ''
                      ) : col.key === 'varPct' ? (
                        renderTotalVarPct(totals.varPct)
                      ) : (
                        <strong>{totals[col.key] ? formatAmount(totals[col.key]) : '0'}</strong>
                      )}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default SimpleTable 